import { ApiError, REQUEST_TIMED_OUT, demoApi, problemMessage } from "@/api/client";
import type { AllocationItem } from "@/api/types";
import { withDrainLock, DRAIN_LOCKS } from "./drainLock";
import { createPunchOutboxStore, type PunchOutboxStore, type QueuedPunch } from "./outboxStore";
import { registerDrainTriggers } from "./drainTriggers";

/**
 * A queued punch the server refused OUTRIGHT once it finally replayed — a 4xx, not a
 * network problem. By the time anything can render this the item is already gone from
 * the queue (see `drainOnce` below), so this is delivered as its own event rather than
 * left for a UI to infer from a row that vanished (ui-breadcrumbs #1: state belongs in
 * the DOM, never inferred).
 */
export interface PunchRefusal {
  clientPunchId: string;
  kind: QueuedPunch["kind"];
  recordedAt: string;
  /** The server's own words (RFC 7807 problem title/detail, via `problemMessage`) —
   *  never a generic message. A worker acting on this needs to know WHY. */
  reason: string;
}

/**
 * ef-024 — the punch-out itself already landed by the time this fires; only the PAIRED
 * allocation was refused (the entry's own hours changed underneath it, a job code was
 * archived mid-flight, etc.). A DIFFERENT shape from `PunchRefusal` on purpose: naming
 * this a `PunchRefusal` would tell a worker their clock-out was rejected, when the thing
 * that actually failed is the "what was this for?" answer riding along with it.
 */
export interface AllocationRefusal {
  entryId: string;
  reason: string;
}

type Listener = (items: QueuedPunch[]) => void;
type RefusalListener = (refusal: PunchRefusal) => void;
type AllocationRefusalListener = (refusal: AllocationRefusal) => void;

function newClientPunchId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Same fallback as offline/outbox.ts's newClientId — older iOS Safari (< 15.4) has no
  // crypto.randomUUID. This id never leaves the device except as a replay's dedupe key,
  // so the (tiny) collision odds are an acceptable trade against a hard runtime error.
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * On-device outbox for a punch (clock in/out) the phone could not deliver — the
 * punch-clock counterpart to `offline/outbox.ts` (ef-002/ef-003's entry queue), and
 * deliberately built on the SAME three pieces that queue already proved out:
 * `outboxStore.ts` (verify-by-real-write store selection), `drainLock.ts` (one tab
 * drains at a time, across tabs), and `drainTriggers.ts` (backoff poll + online +
 * load — including the "a throw must not end the retry loop" fix documented there).
 * A plain singleton, not React state, for the same reason as `outbox`: the queue has
 * to keep draining whether or not `TimeClock` happens to be mounted.
 *
 * WHERE THIS DELIBERATELY DIVERGES FROM `offline/outbox.ts`'s OWN `drainOnce`:
 * that queue's replay is NOT idempotent server-side, so on any failure that is not a
 * clean "still offline" it leaves the row queued and moves on (a permanent choice would
 * risk silently dropping a visitor's entry it had no way to retry safely). A punch
 * replay IS idempotent (ef-016's `clientPunchId` — `TimeEndpoints.cs`
 * `FindPunchInByClientIdAsync` / `FindPunchOutByClientIdAsync`), which changes the right
 * answer in both directions:
 *   - A genuine REFUSAL (4xx) is a real, permanent answer — "already clocked in
 *     elsewhere", "not clocked in" — retrying it would never succeed, so this drops the
 *     item and surfaces WHY, rather than leaving it queued forever the way the entry
 *     outbox leaves a rejected row.
 *   - An AMBIGUOUS failure (a timeout, or a 5xx) is safe to hold and retry precisely
 *     BECAUSE a duplicate replay is a no-op server-side — so this stops the whole drain
 *     and keeps the item, the same as a demonstrated network failure, rather than
 *     guessing at the outcome.
 */
class PunchOutboxManager {
  private storePromise: Promise<PunchOutboxStore> | null = null;
  private listeners = new Set<Listener>();
  private refusalListeners = new Set<RefusalListener>();
  private allocationRefusalListeners = new Set<AllocationRefusalListener>();
  private draining = false;

  private getStore(): Promise<PunchOutboxStore> {
    if (!this.storePromise) this.storePromise = createPunchOutboxStore();
    return this.storePromise;
  }

  async list(): Promise<QueuedPunch[]> {
    const store = await this.getStore();
    return store.all();
  }

  /** Live view of the queue, oldest first — what `hooks/usePunchOutbox.ts` renders. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** One-shot notifications for a dropped, refused replay — see `PunchRefusal` above. */
  subscribeRefusals(fn: RefusalListener): () => void {
    this.refusalListeners.add(fn);
    return () => {
      this.refusalListeners.delete(fn);
    };
  }

  /** One-shot notifications for a refused ALLOCATION — see `AllocationRefusal` above. */
  subscribeAllocationRefusals(fn: AllocationRefusalListener): () => void {
    this.allocationRefusalListeners.add(fn);
    return () => {
      this.allocationRefusalListeners.delete(fn);
    };
  }

  private async notify(): Promise<void> {
    const items = await this.list();
    for (const fn of this.listeners) fn(items);
  }

  private notifyRefusal(refusal: PunchRefusal): void {
    for (const fn of this.refusalListeners) fn(refusal);
  }

  private notifyAllocationRefusal(refusal: AllocationRefusal): void {
    for (const fn of this.allocationRefusalListeners) fn(refusal);
  }

  /**
   * ef-024 — attach "what this was for" to a punch-out THIS DEVICE already queued but has
   * not yet delivered. Never sent to the network here — there is no server row to PUT
   * against until the paired punch-out itself lands (`drainOnce` below does that PUT,
   * once it has the real id back). A no-op if the item already drained (or never
   * existed) between the panel opening and the worker tapping a code — the same
   * "ambiguous, do not block the worker" posture `enqueue`/`drainOnce` already take,
   * rather than throwing over a race nobody can act on from here.
   */
  async attachAllocations(clientPunchId: string, allocations: AllocationItem[]): Promise<void> {
    const store = await this.getStore();
    const item = (await store.all()).find((i) => i.clientPunchId === clientPunchId);
    if (!item) return;
    await store.add({ ...item, allocations });
  }

  /**
   * Queue a punch the live attempt could not deliver
   * (`ApiError.neverReachedTheServer` — see `TimeClock.tsx`'s `punch()`).
   * `recordedAt` MUST be the phone's clock captured by the caller AT THE TAP, before
   * this call — never re-derived here, or a slow enqueue would silently shift the
   * stamp the server is later asked to trust as the shift boundary.
   */
  async enqueue(item: Omit<QueuedPunch, "clientPunchId" | "queuedAt">): Promise<string> {
    const store = await this.getStore();
    const clientPunchId = newClientPunchId();
    await store.add({ ...item, clientPunchId, queuedAt: Date.now() });
    await this.notify();
    // ef-024 — the caller needs this back to attach an allocation to THIS queued item
    // later (`attachAllocations` above), once the worker has picked a job code on the
    // panel that opens after the tap this enqueue is servicing.
    return clientPunchId;
  }

  /**
   * Replay the outbox in order (oldest first) — sequential and awaited one at a time,
   * exactly like `offline/outbox.ts`'s own `drain`, and for the identical reason: only
   * that makes "in order" true rather than merely likely. The guard flag and the
   * cross-tab Web Lock below prevent two overlapping drains — one instance racing
   * itself, or two tabs racing each other — from replaying the same queued punch twice.
   */
  async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      await withDrainLock(DRAIN_LOCKS.punches, async () => {
        try {
          await this.drainOnce();
        } finally {
          // A tab queued behind the lock may find the store already emptied by the other tab and
          // process nothing (or bail still-offline); without this its subscribers keep a stale view.
          await this.notify();
        }
      });
    } finally {
      this.draining = false;
    }
  }

  /** One pass over the queue. Only ever called while the punch drain lock is held. */
  private async drainOnce(): Promise<void> {
    const store = await this.getStore();
    const items = await store.all();

    for (const item of items) {
      try {
        if (item.kind === "in") {
          await demoApi.punchIn({
            latitude: item.latitude,
            longitude: item.longitude,
            clientPunchId: item.clientPunchId,
            recordedAt: item.recordedAt,
          });
        } else {
          const result = await demoApi.punchOut({
            latitude: item.latitude,
            longitude: item.longitude,
            workerNote: item.workerNote,
            breakMinutes: item.breakMinutes,
            clientPunchId: item.clientPunchId,
            recordedAt: item.recordedAt,
          });

          // ef-024 — the paired "what this was for", if the worker answered one while
          // this punch-out was still sitting in the queue (`attachAllocations` above).
          // ONLY once the punch-out itself is a real, filed row (`result.recorded` —
          // never on the "closed but discarded" 0.00h path, which carries no `id` to PUT
          // against at all) — mirrors the online path in `TimeClock.tsx`, which offers
          // the panel on exactly that same condition.
          //
          // ITS OWN try/catch, deliberately NOT the outer one: the punch-out above has
          // ALREADY SUCCEEDED by the time this runs, so a failure here must never be
          // read as "the punch-out failed" (the outer catch's ambiguous-failure branch
          // would re-attempt an already-landed punch-out, and its refusal branch would
          // tell the worker their CLOCK-OUT was refused, which would be a lie). A network
          // failure here is dropped rather than retried — the allocation is not
          // idempotent-safe to replay blind the way the punch itself is, and there is no
          // longer a queued punch-out to hang a retry off of.
          if (item.allocations && item.allocations.length > 0 && result.recorded && result.id) {
            try {
              await demoApi.putTimeAllocations(result.id, { allocations: item.allocations });
            } catch (allocationErr) {
              this.notifyAllocationRefusal({
                entryId: result.id,
                reason: problemMessage(allocationErr, "The server refused that job code split."),
              });
            }
          }
        }
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.neverReachedTheServer || err.status === REQUEST_TIMED_OUT || err.status >= 500)
        ) {
          // Held like an outage — genuinely offline, a dead proxy, a timeout, or a 5xx
          // are all ambiguous-or-worse about whether this landed, and a retried replay
          // of the SAME clientPunchId is a safe no-op if it already did. Stop here, IN
          // ORDER, so nothing later in the queue is attempted ahead of this one — the
          // next drain trigger (backoff, 'online', or a future enqueue) resumes from
          // this same item rather than skipping ahead.
          return;
        }
        if (err instanceof ApiError) {
          // A genuine, permanent refusal (4xx) — idempotency does not help here, the
          // server has already made its decision and a retry would return the same
          // answer forever. Drop it and say why, rather than leaving a worker's
          // "pending" banner promising a sync that will never succeed.
          await store.remove(item.clientPunchId);
          this.notifyRefusal({
            clientPunchId: item.clientPunchId,
            kind: item.kind,
            recordedAt: item.recordedAt,
            reason: problemMessage(err, "The server refused this punch."),
          });
          await this.notify();
          continue;
        }
        // Not an ApiError at all — an unexpected thrown value. Leave it queued rather
        // than guessing at what happened; the next drain attempt gets another chance.
        console.warn("A punch drain attempt failed unexpectedly; retrying with backoff.", err);
        return;
      }

      await store.remove(item.clientPunchId);
      await this.notify();
    }
  }
}

export const punchOutbox = new PunchOutboxManager();

registerDrainTriggers(punchOutbox);
